import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import Dashboard from '../../components/Dashboard';
import MenuContainer from '../../components/custom-components/menu/MenuContainer';
import { PATHS } from '../../router/routes';
import {
    Box,
    Flex,
    Image,
    Stack,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Textarea,
    useToast,
    Button,
} from '@chakra-ui/react';
import ZaraButton from '../../components/custom-components/buttons/Button';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { ChangeTrainingGuide } from '../../graphql/mutations/ChangeTrainingGuide';
import { useNavigate, useParams } from 'react-router-dom';
import { useError } from '../../error-provider/ErrorProvider';
import { SERVER_WS_URL } from '../../utils/constants';
import { TypingDots } from '../../components/custom-components/icons/TypingDot';
import Input from '../../components/custom-components/Inputs/Input';
import sendIcon from '../../assets/icons/send.svg';
import AgentResponseModal from '../../components/modals/AgentResponseModal';
import Messages from '../../components/page-components/agents/Messages';
import MainContainer from '../../components/containers/MainContainer';
import StepperContainer from '../../components/containers/StepperContainer';
import LogoutButton from '../../components/custom-components/buttons/LogoutButton';
import { GetUserAgents } from '../../graphql/queries/GetUserAgents';

const TrainingGuide = ({ isTestChat, onFinishStepper }) => {
    const toast = useToast();
    const router = useNavigate();
    const { showError } = useError();

    const { id } = useParams();

    const [guideValue, setGuideValue] = useState('');

    const [socket, setSocket] = useState(null);

    const guideRef = useRef();

    const apolloClient = useApolloClient();

    const [tabIndex, setTabIndex] = useState(0);

    const [isAgentTyping, setIsAgentTyping] = useState(false);
    const [isOpenPopover, setIsOpenPopover] = useState(false);
    const [userMessage, setUserMessage] = useState({ answer: '', isSendingFix: false });

    const [subscriptionData, setSubscriptionData] = useState([]);

    const [fixText, setFixText] = useState('');

    const endOfChat = useRef();

    const { data: userAgents } = useQuery(GetUserAgents, {
        onError(error) {
            showError(undefined, error);
        },
    });

    const verifyId = id ?? userAgents?.getUserAgents?.agents[0]?.id ?? '';

    const { agentGuide, agentName, agentId } = useMemo(() => {
        const currentAgent = userAgents?.getUserAgents?.agents?.find((agent) => agent.id === verifyId);

        const agentGuide = currentAgent?.trainingGuide ?? '';
        const agentName = currentAgent?.name ?? '';
        const agentId = currentAgent?.id;

        return {
            agentGuide,
            agentName,
            agentId,
        };
    }, [userAgents, verifyId]);

    const [onChangeGuide] = useMutation(ChangeTrainingGuide, {
        onError(error) {
            showError(undefined, error);
        },
    });

    useEffect(() => {
        if (guideRef.current && !tabIndex) {
            guideRef.current.scrollTop = guideRef.current.scrollHeight;
        }
    }, [tabIndex]);

    useEffect(() => {
        if (agentGuide) {
            setGuideValue(agentGuide);
        }
    }, [agentGuide]);

    const onSubmit = useCallback(async (isTestChat) => {
        const variables = {
            newGuide: guideValue,
            agentId: agentId,
        };
        await onChangeGuide({
            variables: variables,
            async onCompleted(data) {
                if (data.changeTrainingGuide.__typename === 'SuccessType') {
                    toast({
                        title: data.changeTrainingGuide.message,
                        status: 'success',
                        isClosable: true,
                    });
                    setSocket(null);
                    setSubscriptionData([]);

                    apolloClient.cache.modify({
                        id: `AgentType:${agentId}`,
                        fields: {
                            initialMessage() {
                                return guideValue;
                            },
                        },
                    });

                    if (!isTestChat) {
                        router(PATHS.MY_AGENT);
                    }
                }
                if (data.changeTrainingGuide.__typename === 'ErrorType') {
                    toast({
                        title: data.changeTrainingGuide.message,
                        status: 'error',
                        isClosable: true,
                    });
                }
            },
        });
    }, [guideValue, agentId, onChangeGuide, toast, apolloClient, router]);

    useEffect(() => {
        endOfChat?.current?.scrollIntoView({
            behavior: 'auto',
            block: 'center',
        });
    }, [subscriptionData, isAgentTyping]);

    useEffect(() => {
        if (socket) return;

        if (agentId) {
            const newSocket = new WebSocket(`${SERVER_WS_URL}/ws-agent-chat/${agentId}/`);

            newSocket.addEventListener('message', (event) => {
                setSubscriptionData((prev) => [...prev, JSON.parse(event.data)]);
            });
            setSocket(newSocket);
        }

        return () => {
            socket?.close();
        };
    }, [agentId, socket]);

    useEffect(() => {
        if (!subscriptionData.length || subscriptionData?.slice(-1)[0]?.audio_preview !== null) {
            setIsAgentTyping(true);
        } else {
            setIsAgentTyping(false);
        }
    }, [subscriptionData]);

    useEffect(() => {
        if (userMessage?.isSendingFix) {
            onSubmit(true);
            setUserMessage((prev) => ({ ...prev, isSendingFix: false }));
        }
    }, [userMessage, onSubmit]);

    const onHandleFix = () => {
        setGuideValue((prev) => prev + '\n\nClarification: ' + fixText);
        setUserMessage((prev) => ({ ...prev, isSendingFix: true }));
        setIsOpenPopover(false);
        setFixText('');
        setTabIndex(0);
        setSocket(null);
        setSubscriptionData([]);
    };

    const handleSendMessage = () => {
        socket?.send(userMessage?.answer);
        setSubscriptionData((prev) => [...prev, userMessage]);
        setUserMessage((prev) => ({ ...prev, answer: '' }));
    };

    const onEnterPress = (e) => {
        if (e.keyCode === 13 && e.shiftKey === false && userMessage?.answer?.trim().length) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const trainingGuide = (
        <Box>
            <Box>
                <Textarea
                    _focus={{ borderColor: 'transparent', boxShadow: 'none' }}
                    ref={guideRef}
                    onChange={(e) => setGuideValue(e.target.value)}
                    fontSize='16px'
                    lineHeight='20px'
                    fontWeight='400'
                    color='#718096'
                    border='none'
                    value={guideValue}
                    css={{
                        '@media only screen and (min-height: 700px)': {
                            minHeight: '550px',
                        },
                        '@media only screen and (max-height: 700px)': {
                            minHeight: '360px',
                        },
                        '@media only screen and (max-width: 360px)': {
                            minHeight: '420px',
                        },
                    }}
                    p='8px 24px'
                />
            </Box>
            <Flex p='16px 24px' mb={isTestChat ? '0px' : '100px'} justifyContent='center' alignItems='center'>
                {!isTestChat && (
                    <ZaraButton
                        onClick={(e) => {
                            e.stopPropagation();

                            router(PATHS.MY_AGENT);
                        }}
                        variant='secondary'
                        mr='10px'
                    >
                        Cancel
                    </ZaraButton>
                )}

                <ZaraButton
                    {...(isTestChat && { width: 'full' })}
                    onClick={(e) => {
                        e.stopPropagation();

                        onSubmit(isTestChat);
                    }}
                >
                    Update
                </ZaraButton>
            </Flex>
            {isTestChat && (
                <Box p='0px 20px'>
                    <Button
                        fontSize={{ base: '14px', sm: '10px', md: '12px', xl: '16px' }}
                        onClick={onFinishStepper}
                        bg='#FD7E14'
                        color='#FFFFFF'
                        w='full'
                        _hover={{
                            bg: 'orange.500',
                        }}
                    >
                        Start Trial To Get a Phone Number for your Agent
                    </Button>
                    <LogoutButton variant='mobile' />
                </Box>
            )}
        </Box>
    );

    const agentChat = (
        <Stack
            onClick={() => setIsOpenPopover(false)}
            pos='relative'
            justifyContent='space-between'
            overflow='hidden'
            h='calc(100vh - 175px)'
        >
            <Stack spacing='12px' p='10px' overflowY='auto'>
                <AgentResponseModal
                    isOpenPopover={isOpenPopover}
                    onClose={() => setIsOpenPopover(false)}
                    setFixText={setFixText}
                    onHandleFix={onHandleFix}
                />

                <Messages
                    isTestChat={isTestChat}
                    setIsOpenPopover={setIsOpenPopover}
                    subscriptionData={subscriptionData}
                />
                {isAgentTyping && <TypingDots />}
                <Box ref={endOfChat}></Box>
            </Stack>
            <Input
                borderRight='none'
                borderLeft='none'
                _focus={{ borderColor: 'transparent', boxShadow: 'none' }}
                rightIcon={<Image onClick={handleSendMessage} src={sendIcon} w='24px' h='24px' />}
                borderRadius='0px'
                borderBottom='none'
                placeholder='Write your message'
                onKeyDown={onEnterPress}
                value={userMessage.answer}
                onChange={(e) => setUserMessage((prev) => ({ ...prev, answer: e.target.value }))}
            />
        </Stack>
    );

    const content = (
        <Box w='100%' mt={{ base: isTestChat ? '75px' : '70px', sm: '110px' }}>
            <Tabs index={tabIndex} onChange={setTabIndex} p='0px'>
                <TabList p='0px'>
                    <Tab p='0px' w='50%'>
                        Current Training Guide
                    </Tab>
                    <Tab p='0px' w='50%'>
                        {isTestChat ? 'Test Agent' : agentName}
                    </Tab>
                </TabList>

                <TabPanels p='0px'>
                    <TabPanel p='0px'>{trainingGuide}</TabPanel>
                    <TabPanel p='0px'>{agentChat}</TabPanel>
                </TabPanels>
            </Tabs>
        </Box>
    );

    return (
        <Dashboard title={isTestChat ? '' : PATHS.TRAINING_GUIDE}>
            {isTestChat ? (
                <MainContainer>
                    <StepperContainer>{content}</StepperContainer>
                </MainContainer>
            ) : (
                <MenuContainer title={PATHS.TRAINING_GUIDE}>{content}</MenuContainer>
            )}
        </Dashboard>
    );
};

export default TrainingGuide;
